import { Component } from '@theme/component';

/**
 * @typedef {Object} SfSweepsPopoverRefs
 * @property {HTMLElement} trigger
 * @property {HTMLElement} panel
 */

/**
 * Toggles the prize-details popup on a product card's entries pill.
 *
 * The pill sits inside the card's `<a>`, so a tap on it has to cancel the link
 * navigation before opening the popup. Only one popup is open at a time; an
 * outside click or Escape closes it. The document listeners are only attached
 * while the popup is open, so a grid of cards pays nothing until one is tapped.
 *
 * On desktop the stylesheet also shows the popup on hover. A popup opened by a
 * click closes when the mouse leaves, so it never stays stuck open after the
 * hover that went with it has ended.
 *
 * @extends {Component<SfSweepsPopoverRefs>}
 */
class SfSweepsPopover extends Component {
  /** @type {SfSweepsPopover | null} */
  static #open = null;

  /** @type {AbortController | null} */
  #controller = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#close();
  }

  /** @param {MouseEvent} event */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasAttribute('open')) {
      this.#close();
    } else {
      this.#show();
    }
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.toggle(event);
  }

  /**
   * Called by on:pointerleave on the element itself.
   *
   * @param {PointerEvent} event
   */
  handlePointerLeave(event) {
    if (event.pointerType === 'mouse' && this.hasAttribute('open')) this.#close();
  }

  #show() {
    SfSweepsPopover.#open?.#close();
    SfSweepsPopover.#open = this;

    this.setAttribute('open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.refs.panel.hidden = false;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(
      'click',
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        this.#close();
        this.refs.trigger.focus();
      },
      { signal }
    );
  }

  #close() {
    this.#controller?.abort();
    this.#controller = null;
    if (SfSweepsPopover.#open === this) SfSweepsPopover.#open = null;

    this.removeAttribute('open');
    this.refs.trigger?.setAttribute('aria-expanded', 'false');
    if (this.refs.panel) this.refs.panel.hidden = true;
  }
}

if (!customElements.get('sf-sweeps-popover')) {
  customElements.define('sf-sweeps-popover', SfSweepsPopover);
}
